import React, { useState, useRef, useEffect } from "react";
import { View, Text, TextInput, StyleSheet, Platform, Modal, Image, TouchableOpacity } from "react-native";
import { useTranslation } from "../../../../../i18n/useTranslation";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import WarningIcon from "../../../../../../../../assets/images/WarningIcon.svg";
import { capturePosthogEvent } from "../../../../../utils/analytics";
import { inputAssistDisabledProps } from "../../../common/FloatingTextInputComponent";

type PinInputStepProps = {
  handleForgotPassword?: () => void;
  attemptsLeft?: number;
  locked?: boolean;
  showAttemptsWarning?: boolean;
  resetSignal?: number;
  onContinue?: () => void;
  /** API error from PIN verification (e.g. postAuthConsumer) */
  apiError?: string;
  testID?: string | undefined;
};

export default function PinInputStep({ attemptsLeft, locked, showAttemptsWarning = true, handleForgotPassword, resetSignal = 0, onContinue, apiError, testID }: PinInputStepProps) {
  const { t } = useTranslation();
  const [pin, setPin] = useState(["", "", "", ""]);
  const inputs = useRef<(TextInput | null)[]>([]);
  const setConsumerPin = useSessionStore((s: any) => s.setConsumerPin);
  const isNewConsumer = useSessionStore((s: SessionState) => (s as any).isNewConsumer);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const hasValidationError = !isNewConsumer && !locked && !!apiError;
  const suppressWebFocusRing = (e: any) => {
    if (Platform.OS !== "web") return;
    const el = e?.target as any;
    if (!el?.style) return;
    el.style.outline = "none";
    el.style.boxShadow = "none";
    el.style.webkitTapHighlightColor = "transparent";
  };

  // 🔑 watch for resetSignal changes
  useEffect(() => {
    setPin(["", "", "", ""]);
    setFocusedIndex(0);
    requestAnimationFrame(() => {
      inputs.current[0]?.focus();
    });
  }, [resetSignal]);


  const handleChange = (text: string, index: number) => {
    // Validate that text contains only digits using string methods (avoid ReDoS)
    // Check each character to ensure it's a digit (0-9) without using regex
    const isValid = text.length === 0 || text.split('').every(char => {
      const charCode = char.charCodeAt(0);
      return charCode >= 48 && charCode <= 57; // '0' is 48, '9' is 57
    });
    
    if (isValid) {
      const newPin = [...pin];
      newPin[index] = text;
      setPin(newPin);
      try { setConsumerPin?.(newPin.join("")); } catch {}

      // Move to next box automatically
      if (text && index < 3) {
        inputs.current[index + 1]?.focus();
      }

      // Backspace handling
      if (!text && index > 0) {
        inputs.current[index - 1]?.focus();
      }

      if (newPin.every(d => d !== "")) {
        onContinue?.();
      }

    }
  };

  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.title}>{t("PIN_INPUT_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("PIN_INPUT_SUBTITLE")}</Text>

      <View style={styles.pinContainer}>
        {pin.map((digit, index) => (
          <View
            style={[
              styles.pinBox,
              {
                borderColor: (locked || hasValidationError)
                  ? Colors.errorRedColor
                  : Colors.black,
                borderWidth: (locked || hasValidationError)
                  ? 1
                  : (focusedIndex === index ? 1 : 0),
              },
            ]}
          >
            <TextInput
              key={index}
              testID={`sendPinInput${index}`}
              ref={(el) => (inputs.current[index] = el)}
              autoFocus={index === 0}
              style={styles.pinInput}
              keyboardType="number-pad"
              maxLength={1}
              secureTextEntry
              value={digit}
              onChangeText={(text) => handleChange(text, index)}
              onFocus={(e: any) => {
                suppressWebFocusRing(e);
                setFocusedIndex(index);
              }}
              {...inputAssistDisabledProps}
            />
          </View>
        ))}
      </View>

      {/* {apiError ? (
        <View style={styles.apiErrorContainer}>
          <Text style={styles.apiErrorText} testID="pinApiError">{apiError}</Text>
        </View>
      ) : null} */}
      {isNewConsumer ? null : locked ? (
        <View style={{ flexDirection: 'row', alignItems: 'center'}}>
          <WarningIcon width={16} height={16}/>
          <Text style={styles.locked}>{t("ACCOUNT_LOCKED")}</Text>
        </View>
      ) : showAttemptsWarning ? (
        <Text style={[styles.attempts, hasValidationError && styles.attemptsError]}>
          {attemptsLeft} {attemptsLeft === 1 ? "attempt" : "attempts"} remaining
        </Text>
      ) : null}
      {/* {pin.join("").length > 0 && pin.join("").length < 4 && (
        <Text style={styles.error}>{t("PIN_INPUT_ERROR")}</Text>
      )} */}
      {/* {!locked ? (
        <Text style={styles.attempts}>
          {attemptsLeft} {attemptsLeft === 1 ? "attempt" : "attempts"} remaining
        </Text>
      ) : (
        <Text style={styles.locked}>{t("ACCOUNT_LOCKED")}</Text>
      )} */}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 32
  },
  pinContainer: {
    flexDirection: "row",
    marginBottom: 8,
    justifyContent: 'space-between',
    // backgroundColor: 'yellow',
    
  },
  pinInput: {
    // width: 55,
    // height: 55,
    borderRadius: 8,
    flex: 1,
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    backgroundColor: Colors.searchCompanyBackground,
    ...(Platform.OS === 'web' && {
      outlineWidth: 0,
      outline: 'none',
      border: 'none',
      boxShadow: 'none',
    } as any),
  },
  pinBox: {
    width: 56,
    height: 56,
    borderRadius: 8,
    textAlign: "center",
  },
  error: {
    color: "red",
    fontSize: 12,
    marginTop: 8,
  },
  attempts: {
    fontSize: 14,
    color: Colors.black,
  },
  attemptsError: {
    color: Colors.errorRedColor,
  },
  locked: {
    fontSize: 14,
    fontFamily: 'Rubik',
    color: Colors.errorRedColor,
    fontWeight: "400",
    lineHeight: 20,
    paddingLeft: 8
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)"
  },
  modalContent: {
    backgroundColor: "white", padding: 20,
    borderTopLeftRadius: 20, borderTopRightRadius: 20,
  },
  accountLock: {
    fontSize: 24,
    fontWeight: "500",
    lineHeight: 28,
    fontFamily: 'Rubik',
    marginVertical: 26,
    color: Colors.accountLock,
  },
  accountLockInfo: {
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 18,
    fontFamily: 'Rubik',
    marginBottom: 48,
    color: Colors.accountLock,
  },
  button: {
    backgroundColor: Colors.titleTextColor,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
  },
  buttonText: {
    color: Colors.white,
    fontSize: 16,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 20,
    paddingHorizontal: 100
  },
});
